import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { SHARE_STYLES } from '@/components/ShareButton';
import { Globe, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'h-11 bg-[#0f1724] border-white/15 text-white placeholder:text-white/30 focus:border-[#C6A14A]';

const RequestPanel = ({ gallerySlug, onDone }) => {
  const { isAuthenticated, user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated && (name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(email.trim()))) {
      setError('Please enter your name and a valid email so we can reply.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await (isAuthenticated ? authFetch : fetch)(`${API_BASE}/api/listings/requests`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gallery_slug: gallerySlug, ...(isAuthenticated ? {} : { name: name.trim(), email: email.trim() }) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Something went wrong. Please try again.');
      setSent(data.already_requested ? 'already' : 'new');
      onDone?.();
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="text-center py-2" data-testid="pw-request-success">
        <CheckCircle2 className="h-8 w-8 text-[#C6A14A] mx-auto" />
        <p className="text-white font-medium mt-2">{sent === 'already' ? 'Already requested' : 'Request sent'}</p>
        <p className="text-sm text-[#E8E4DE]/60 mt-1">We'll be in touch about a website for this listing.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate className="space-y-3" data-testid="pw-request-panel">
      <p className="font-serif text-xl text-white leading-snug">Get a single-property website for this listing</p>
      <p className="text-sm text-[#E8E4DE]/65">A dedicated page with the full gallery, key details, a map and the agent's contact info, ready to share with buyers.</p>
      {isAuthenticated ? (
        <p className="text-xs text-[#E8E4DE]/50">We'll reply to {user?.email}.</p>
      ) : (
        <>
          <Input aria-label="Your name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="pw-request-name" />
          <Input aria-label="Your email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="pw-request-email" />
        </>
      )}
      {error && <p role="alert" className="flex items-start gap-2 text-sm text-red-400" data-testid="pw-request-error"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{error}</p>}
      <button type="submit" disabled={sending} data-testid="pw-request-submit"
        className="w-full h-11 rounded-lg bg-[#C6A14A] text-[#050816] font-medium hover:bg-[#D4AF5A] transition-colors inline-flex items-center justify-center gap-2">
        {sending && <Loader2 className="h-4 w-4 animate-spin" />}Request Property Website
      </button>
    </form>
  );
};

// Sits next to Share: opens the listing page if one exists, otherwise a small request panel.
export const PropertyWebsiteButton = ({ gallerySlug, className = '' }) => {
  const [listingSlug, setListingSlug] = useState(undefined);
  useEffect(() => {
    fetch(`${API_BASE}/api/listings/by-gallery/${gallerySlug}`).then((r) => r.json()).then((d) => setListingSlug(d.slug || null)).catch(() => setListingSlug(null));
  }, [gallerySlug]);

  const cls = `inline-flex items-center gap-1.5 text-sm whitespace-nowrap transition-colors active:scale-95 ${SHARE_STYLES.outline} ${className}`;
  const content = <><Globe className="h-4 w-4" /><span className="sm:hidden">Website</span><span className="hidden sm:inline">Property Website</span></>;
  if (listingSlug === undefined) return null;
  if (listingSlug) return <Link to={`/listing/${listingSlug}`} className={cls} data-testid="property-website-btn">{content}</Link>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cls} data-testid="property-website-btn">{content}</button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[min(92vw,360px)] bg-[#0a1120] border-[#C6A14A]/30 p-5 z-[1001]">
        <RequestPanel gallerySlug={gallerySlug} />
      </PopoverContent>
    </Popover>
  );
};
